// 🏷️ B261 (AI_117) — Selo de status (statusGeral) derivado do TEXTO da análise,
// ROBUSTO à deriva de formatação do modelo.
// ─────────────────────────────────────────────────────────────────────────────
// O selo do Resumo Executivo ("estável"/"atenção"/"crítico") é deduzido da
// gravidade/urgência que o próprio texto declara. A versão antiga usava
// substring EXATA (`texto.includes("gravidade: grave")`) e quebrava quando o
// modelo escrevia em markdown ("Gravidade: **GRAVE**") ou variava o separador →
// o selo caía no default "atenção" mesmo com a narrativa concluindo "GRAVE".
// Aqui normalizamos (sem acento, sem markdown, espaços colapsados) e casamos por
// SIGNIFICADO, não por marcador exato. Lição [[regressao-visual-formato-ia-deriva]].
// Função PURA (sem IA, sem efeito colateral) → testável isoladamente.

export type StatusGeral = "estavel" | "atencao" | "critico";

// Normaliza p/ busca: remove acentos, marcações markdown (* _ ` #) e colapsa
// espaços, em minúsculas. Assim "Gravidade: **GRAVE**" → "gravidade: grave".
function normalizarParaStatus(texto: string): string {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // acentos
    .replace(/[*_`#~]/g, "") // marcação markdown
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Deduz o selo de status a partir do texto da análise.
 * Precedência: CRÍTICO > ESTÁVEL > (default) ATENÇÃO — escalar é o lado seguro
 * num apoio à decisão clínica. Aceita até 4 caracteres não-alfanuméricos entre
 * o rótulo e o valor (colón, traço, espaço, markdown já removido).
 */
export function derivarStatusGeral(texto: string): StatusGeral {
  const t = normalizarParaStatus(texto);
  if (/gravidade[^a-z0-9]{0,4}grave\b/.test(t) || /urgencia[^a-z0-9]{0,4}alta\b/.test(t)) {
    return "critico";
  }
  if (/gravidade[^a-z0-9]{0,4}leve\b/.test(t) || /urgencia[^a-z0-9]{0,4}baixa\b/.test(t)) {
    return "estavel";
  }
  return "atencao";
}

// 🏷️ B275 (AI_120) — GRAVIDADE e URGÊNCIA como eixos SEPARADOS.
// O selo único (derivarStatusGeral) combinava os dois com gravidade em
// precedência → um caso GRAVE porém compensado/estável virava "crítico" e
// superestimava a urgência. Estas funções extraem cada eixo do texto para o
// Resumo Executivo exibir os dois selos lado a lado. Mesma normalização robusta.
export type Gravidade = "leve" | "moderada" | "grave";
export type Urgencia = "baixa" | "media" | "alta";

/** Extrai a GRAVIDADE declarada no texto ("Gravidade: X"). null se ausente. */
export function derivarGravidade(texto: string): Gravidade | null {
  const t = normalizarParaStatus(texto);
  if (/gravidade[^a-z0-9]{0,4}grave\b/.test(t)) return "grave";
  if (/gravidade[^a-z0-9]{0,4}moderad[ao]\b/.test(t)) return "moderada";
  if (/gravidade[^a-z0-9]{0,4}leve\b/.test(t)) return "leve";
  return null;
}

/** Extrai a URGÊNCIA declarada no texto ("Urgência: X"). null se ausente. */
export function derivarUrgencia(texto: string): Urgencia | null {
  const t = normalizarParaStatus(texto);
  if (/urgencia[^a-z0-9]{0,4}alta\b/.test(t)) return "alta";
  if (/urgencia[^a-z0-9]{0,4}media\b/.test(t)) return "media";
  if (/urgencia[^a-z0-9]{0,4}baixa\b/.test(t)) return "baixa";
  return null;
}

// 🫀 B351 passo 5b (AI_153) — SELO DA MODALIDADE DOADOR VIVO.
// ─────────────────────────────────────────────────────────────────────────────
// Gravidade e urgência são eixos do RECEPTOR: um doador saudável não tem
// gravidade a declarar, e "urgência" no prontuário dele seria a urgência de
// outra pessoa. O eixo do doador é ADEQUABILIDADE PARA A DOAÇÃO.
//
// Não é só uma troca de rótulo — é uma trava. Verificado: a regex de gravidade
// casa com uma frase incidental como "esteatose de gravidade leve (~8%)" e
// produziria selo VERDE, que é exatamente a falsa tranquilidade que esta
// modalidade mais comete. Por isso, no doador, derivarGravidade/derivarUrgencia
// NÃO são chamadas.

export type Adequabilidade =
  | "favoravel"
  | "com-ressalvas"
  | "desfavoravel"
  | "indeterminada";

/**
 * Extrai o selo da última linha ("Adequabilidade preliminar: X — justificativa").
 *
 * O rótulo é escrito em caixa MISTA de propósito: em CAIXA ALTA o renderizador
 * da aba trataria a linha como TÍTULO de seção (verificado). A normalização
 * aqui é a mesma do B261 — tolera markdown, acento e espaçamento, porque o
 * formato do modelo deriva com o tempo.
 *
 * `null` = o texto não declarou selo (análise de outra modalidade, ou formato
 * quebrado). Quem chama decide o que fazer — nunca inventamos um selo.
 */
export function derivarAdequabilidadeDoador(
  texto: string
): Adequabilidade | null {
  const t = normalizarParaStatus(texto);
  // "com ressalvas" antes de "favoravel": senão o "favoravel" de uma frase
  // anterior venceria. E "desfavoravel" contém "favoravel" — por isso a ordem
  // e o \b à esquerda importam.
  // "preliminar" é opcional porque o modelo às vezes o omite; os separadores
  // (dois-pontos, espaço, traço) já vêm normalizados acima.
  const m = t.match(
    /adequabilidade(?:\s+preliminar)?[^a-z0-9]{0,4}(com ressalvas|desfavoravel|indeterminada|favoravel)\b/
  );
  if (!m) return null;
  switch (m[1]) {
    case "com ressalvas":
      return "com-ressalvas";
    case "desfavoravel":
      return "desfavoravel";
    case "indeterminada":
      return "indeterminada";
    default:
      return "favoravel";
  }
}

/**
 * Mapeia a adequabilidade para o `statusGeral` legado.
 *
 * Existe porque `resumo.statusGeral` é lido por consumidores que não conhecem
 * doador — a cor do card do histórico (AbaAnaliseIA), o painel de comparação
 * entre análises (PainelDelta) e o few-shot. Sem o mapa, todos cairiam no
 * default "atenção".
 *
 * COM RESSALVAS e INDETERMINADA viram "atenção" (não "estável"): um selo que
 * não é favorável nunca deve pintar de verde.
 */
export function statusGeralDeAdequabilidade(
  a: Adequabilidade | null
): StatusGeral {
  if (a === "favoravel") return "estavel";
  if (a === "desfavoravel") return "critico";
  return "atencao";
}
